/**
 * Device fingerprint helpers.
 * --------------------------------------------------------------------
 * Produces a deterministic, lightweight browser fingerprint and manages
 * the trusted-device cookie returned by the backend after OTP verify.
 *
 * No external dependencies — uses a small djb2 + xor mix to produce a
 * hex digest from the assembled signal string.
 */

const INSTALL_ID_KEY = 'cms_install_id';
const DEVICE_COOKIE_KEY = 'cms_device_cookie';

function uuidV4() {
  // RFC4122 v4 generator using crypto.getRandomValues when available.
  try {
    const cryptoObj =
      typeof crypto !== 'undefined' && crypto.getRandomValues ? crypto : null;
    if (cryptoObj && typeof cryptoObj.randomUUID === 'function') {
      return cryptoObj.randomUUID();
    }
    if (cryptoObj) {
      const buf = new Uint8Array(16);
      cryptoObj.getRandomValues(buf);
      buf[6] = (buf[6] & 0x0f) | 0x40;
      buf[8] = (buf[8] & 0x3f) | 0x80;
      const hex = Array.from(buf).map((b) => b.toString(16).padStart(2, '0'));
      return (
        hex.slice(0, 4).join('') +
        '-' +
        hex.slice(4, 6).join('') +
        '-' +
        hex.slice(6, 8).join('') +
        '-' +
        hex.slice(8, 10).join('') +
        '-' +
        hex.slice(10, 16).join('')
      );
    }
  } catch (_) {
    /* fall through */
  }
  // Math.random fallback (still RFC4122-shaped, lower entropy).
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function getInstallId() {
  try {
    let id = localStorage.getItem(INSTALL_ID_KEY);
    if (!id) {
      id = uuidV4();
      localStorage.setItem(INSTALL_ID_KEY, id);
    }
    return id;
  } catch (_) {
    return 'no-install-id';
  }
}

function hashString(str) {
  // djb2 variant with xor; returns 16-char hex digest by combining two
  // running hashes for a little more entropy than a single 32-bit word.
  let h1 = 5381;
  let h2 = 52711;
  for (let i = 0; i < str.length; i++) {
    const c = str.charCodeAt(i);
    h1 = ((h1 << 5) + h1) ^ c; // h1 * 33 ^ c
    h2 = ((h2 << 5) + h2) + c;
    // keep within 32-bit range
    h1 = h1 | 0;
    h2 = h2 | 0;
  }
  const toHex = (n) => (n >>> 0).toString(16).padStart(8, '0');
  return toHex(h1) + toHex(h2);
}

export function getDeviceFingerprint() {
  let tz = '';
  try {
    tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch (_) {
    tz = '';
  }
  const parts = [
    typeof navigator !== 'undefined' ? navigator.userAgent || '' : '',
    typeof navigator !== 'undefined' ? navigator.language || '' : '',
    typeof window !== 'undefined' && window.screen ? String(window.screen.width || '') : '',
    typeof window !== 'undefined' && window.screen ? String(window.screen.height || '') : '',
    tz,
    getInstallId(),
  ];
  return hashString(parts.join('|'));
}

export function getDeviceCookie() {
  try {
    return localStorage.getItem(DEVICE_COOKIE_KEY) || null;
  } catch (_) {
    return null;
  }
}

export function setDeviceCookie(c) {
  try {
    if (c) localStorage.setItem(DEVICE_COOKIE_KEY, c);
    else localStorage.removeItem(DEVICE_COOKIE_KEY);
  } catch (_) {
    /* ignore */
  }
}

const fingerprintApi = {
  getDeviceFingerprint,
  getDeviceCookie,
  setDeviceCookie,
};

export default fingerprintApi;
